// "Talk to HR": an employee hands a question to a person. The request carries who they are (the
// only place Ask HR attaches identity to what someone asked), a category, an optional note, and,
// if they choose, the conversation it came from. HR is emailed and works it in the Requests queue.
// The employee can follow it in Ask HR: its status, and any comment HR addresses to them. HR's own
// notes, and who in HR owns the request, stay with HR.
//
// Status follows who spoke last: HR's comment moves a request to "waiting" (on the employee), the
// employee's reply moves it back to "in_progress" (shown as Open), even from resolved.

import { BadRequestError } from "./sources.js";

export const CATEGORIES = {
  leave: "Leave or time off",
  pay: "Pay or benefits",
  policy: "A policy question",
  concern: "A workplace concern",
  other: "Something else",
} as const;

export type Category = keyof typeof CATEGORIES;
// "in_progress" is stored as before and shown as Open.
export type EscalationStatus = "new" | "in_progress" | "waiting" | "resolved";
export type Channel = "web" | "slack";

export const STATUS_LABELS: Record<EscalationStatus, string> = {
  new: "New",
  in_progress: "Open",
  waiting: "Waiting on employee",
  resolved: "Resolved",
};

// The same statuses as the employee sees them: what's happening, in their words.
export const EMPLOYEE_STATUS_LABELS: Record<EscalationStatus, string> = {
  new: "Sent to HR",
  in_progress: "HR is looking into it",
  waiting: "HR replied",
  resolved: "Resolved",
};

// Someone in HR who can own a request: a member of Ask HR - Managers or Ask HR - Agents.
export interface Owner {
  oid: string;
  name: string;
  email: string;
}

export interface ExcerptTurn {
  role: "user" | "assistant";
  text: string;
}

// A file HR added to a note or comment. Stored at attachmentKey(request, id); downloaded through
// a short-lived link the API hands out to HR, or to the requester when it's on a comment.
export interface Attachment {
  id: string;
  name: string;
  type: string; // one of ATTACHMENT_TYPES
  size: number; // bytes, as stored
}

export interface EscalationEvent {
  at: string;
  by: string;
  status?: EscalationStatus;
  note?: string;
  // "employee": the note is a comment the requester sees. Absent: a note for HR only (every note
  // written before comments existed is one).
  visibility?: "employee";
  attachments?: Attachment[];
  // "employee": the requester wrote it (a reply, on the web or in Slack). Absent: HR did.
  author?: "employee";
  // The owner changed to this person, or null: the owner was removed. HR only.
  assignee?: { oid: string; name: string } | null;
}

// What HR may attach: documents and pictures, never anything a browser would run.
export const ATTACHMENT_TYPES: Record<string, string> = {
  "application/pdf": "PDF",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "Word",
  "application/msword": "Word",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "Excel",
  "application/vnd.ms-excel": "Excel",
  "text/csv": "CSV",
  "text/plain": "Text",
  "image/png": "PNG",
  "image/jpeg": "JPEG",
};
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
export const MAX_ATTACHMENTS = 5;
export const isAttachmentType = (t: unknown): t is string => typeof t === "string" && Object.hasOwn(ATTACHMENT_TYPES, t);
// A file's type by its extension (browsers and Slack don't always say, or say "octet-stream"),
// else the type it came with if that's allowed. Undefined: not a file Ask HR takes.
const BY_EXTENSION: Record<string, string> = {
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  csv: "text/csv",
  txt: "text/plain",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
};
export const ATTACHMENT_EXTENSIONS = Object.keys(BY_EXTENSION);
export function attachmentTypeFor(name: string, type?: string): string | undefined {
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  return BY_EXTENSION[ext] ?? (isAttachmentType(type) ? type : undefined);
}

export const attachmentKey = (requestId: string, id: string) => `uploads/requests/${requestId}/${id}`;

// A file name safe to store and show: no folders, control characters or quotes, at most 120
// characters (keeping the extension).
export function attachmentName(raw: unknown): string {
  const base = (typeof raw === "string" ? raw : "").split(/[\\/]/).pop() ?? "";
  let name = "";
  for (const ch of base) {
    const c = ch.codePointAt(0) ?? 0;
    name += c < 32 || c === 127 || ch === '"' ? "_" : ch;
  }
  name = name.trim();
  if (!name || name === "." || name === "..") return "attachment";
  if (name.length <= 120) return name;
  const dot = name.lastIndexOf(".");
  const ext = dot > 0 && name.length - dot <= 10 ? name.slice(dot) : "";
  return name.slice(0, 120 - ext.length) + ext;
}

export function attachmentSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export interface Escalation {
  id: string;
  // Short reference for people (#1001). Absent only on requests made before numbers existed,
  // until HR's list numbers them.
  number?: number;
  status: EscalationStatus;
  category: Category;
  note: string;
  channel: Channel;
  requester: { oid: string; name: string; email: string };
  location: string; // "California", "Another US state", "Canada", or "Not set"
  conversationId?: string;
  excerpt: ExcerptTurn[];
  createdAt: string;
  updatedAt: string;
  history: EscalationEvent[];
  emailed: boolean;
  // When the employee removed it from their list in Ask HR. HR keeps the request and sees this.
  hiddenAt?: string;
  // Who in HR owns it. Never shown to the employee.
  assignee?: Owner;
}

export const requestRef = (e: { number?: number | undefined }) => (e.number ? ` #${e.number}` : "");

export interface EscalationInput {
  category: Category;
  note: string;
  conversationId?: string;
  includeConversation: boolean;
}

// Telling an employee HR commented on their request: by email and in Slack, with a link and not
// the comment itself (it stays in Ask HR, where only they and HR can see it).
export interface CommentNotice {
  kind: "notify_comment";
  email: string;
  requestId: string;
  category: Category;
  number?: number;
}

export function commentNoticeText(category: Category, url: string, number?: number): { subject: string; text: string; html: string } {
  const about = CATEGORIES[category].toLowerCase();
  const ref = requestRef({ number });
  const text = `HR replied to your request${ref} about ${about}. Read it in Ask HR: ${url}`;
  const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  return {
    subject: `HR replied to your request${ref}`,
    text: `${text}\n\nReply in Ask HR (you can attach files there), so HR sees it on your request.`,
    html: `<p>HR replied to your request${ref} about ${esc(about)}.</p><p><a href="${esc(url)}">Read it in Ask HR</a></p><p style="color:#666;font-size:12px">Reply in Ask HR (you can attach files there), so HR sees it on your request.</p>`,
  };
}

// Telling the HR Slack channel about a request made on the web (Slack requests post their own
// notice): who and what kind, with a link, never the note.
export interface NewRequestNotice {
  kind: "notify_new";
  requestId: string;
  category: Category;
  name: string;
}

// Telling HR the requester replied, a link and not the reply: the request's owner by email and
// Slack DM, or, when nobody owns it, hr@ and the HR Slack channel.
export interface ReplyNotice {
  kind: "notify_reply";
  requestId: string;
  category: Category;
  name: string;
  number?: number;
  ownerEmail?: string;
}

export function replyNoticeEmail(e: Escalation, appUrl: string): { subject: string; text: string; html: string } {
  const link = `${appUrl}/admin/requests/${e.id}`;
  const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  return {
    subject: `Ask HR: ${e.requester.name} replied on request${requestRef(e)} (${CATEGORIES[e.category]})`,
    text: [`${e.requester.name} replied on their Talk to HR request${requestRef(e)}: ${CATEGORIES[e.category]}.`, "", `Read it and answer in Ask HR: ${link}`].join("\n"),
    html: [
      `<p><strong>${esc(e.requester.name)}</strong> replied on their Talk to HR request${requestRef(e)}: <strong>${esc(CATEGORIES[e.category])}</strong>.</p>`,
      `<p><a href="${esc(link)}">Read it and answer in Ask HR</a>.</p>`,
    ].join("\n"),
  };
}

// What the requester sees of their own request. Never HR's notes, never other requests.
export interface EmployeeRequest {
  id: string;
  number?: number;
  status: EscalationStatus;
  category: Category;
  createdAt: string;
  updatedAt: string;
  summary: string; // the start of their note, for the list and its search
  comments: number;
  lastCommentAt?: string;
}

export interface EmployeeTimelineEntry {
  at: string;
  status?: EscalationStatus;
  // mine: their own reply; otherwise a comment from HR.
  comment?: { by: string; text: string; attachments?: Attachment[]; mine?: true };
}

export interface EmployeeRequestDetail extends EmployeeRequest {
  note: string;
  channel: Channel;
  conversationId?: string;
  excerpt: ExcerptTurn[];
  timeline: EmployeeTimelineEntry[];
}

// Shared with the requester: HR's comments and their own replies.
const isShared = (h: EscalationEvent) => h.visibility === "employee" && Boolean(h.note || h.attachments?.length);
// HR's comments only: what "updates" and the unseen badge count.
const isComment = (h: EscalationEvent) => isShared(h) && h.author !== "employee";

// An attachment the requester may download: on a comment to them, never on an HR-only note.
export function employeeAttachment(e: Escalation, id: string): Attachment | undefined {
  return e.history.filter(isShared).flatMap((h) => h.attachments ?? []).find((a) => a.id === id);
}

export function findAttachment(e: Escalation, id: string): Attachment | undefined {
  return e.history.flatMap((h) => h.attachments ?? []).find((a) => a.id === id);
}

export function forEmployee(e: Escalation): EmployeeRequest {
  const comments = e.history.filter(isComment);
  const last = comments.at(-1);
  return {
    id: e.id,
    ...(e.number ? { number: e.number } : {}),
    status: e.status,
    category: e.category,
    createdAt: e.createdAt,
    updatedAt: e.updatedAt,
    summary: e.note.length > 140 ? `${e.note.slice(0, 140)}…` : e.note,
    comments: comments.length,
    ...(last ? { lastCommentAt: last.at } : {}),
  };
}

export function forEmployeeDetail(e: Escalation): EmployeeRequestDetail {
  const timeline: EmployeeTimelineEntry[] = [];
  let prev: EscalationStatus | undefined;
  for (const h of e.history) {
    // The status changes the employee needs to see: sent, resolved, and opened again. Waiting and
    // Open follow every comment and reply, so those would only repeat the conversation.
    const status = h.status && (h.status === "new" || h.status === "resolved" || prev === "resolved") ? h.status : undefined;
    if (h.status) prev = h.status;
    const comment = isShared(h)
      ? {
          by: h.by,
          text: h.note ?? "",
          ...(h.attachments?.length ? { attachments: h.attachments } : {}),
          ...(h.author === "employee" ? { mine: true as const } : {}),
        }
      : undefined;
    // An HR-only note that came with a status change still shows the change, without the note.
    if (!comment && !status) continue;
    timeline.push({ at: h.at, ...(status ? { status } : {}), ...(comment ? { comment } : {}) });
  }
  return {
    ...forEmployee(e),
    note: e.note,
    channel: e.channel,
    ...(e.conversationId ? { conversationId: e.conversationId } : {}),
    excerpt: e.excerpt,
    timeline,
  };
}

export const MAX_NOTE_CHARS = 2000;
export const MAX_EXCERPT_TURNS = 12;

export function isCategory(v: unknown): v is Category {
  return typeof v === "string" && Object.hasOwn(CATEGORIES, v);
}

export function isStatus(v: unknown): v is EscalationStatus {
  return v === "new" || v === "in_progress" || v === "waiting" || v === "resolved";
}

export function parseEscalationInput(body: unknown): EscalationInput {
  const b = (typeof body === "object" && body !== null ? body : {}) as Record<string, unknown>;
  if (!isCategory(b.category)) throw new BadRequestError("Choose what this is about.");
  const note = typeof b.note === "string" ? b.note.trim() : "";
  if (note.length > MAX_NOTE_CHARS) throw new BadRequestError(`Notes can be up to ${MAX_NOTE_CHARS} characters.`);
  const conversationId = typeof b.conversationId === "string" && b.conversationId ? b.conversationId : undefined;
  const includeConversation = b.includeConversation !== false && Boolean(conversationId);
  if (!note && !includeConversation) throw new BadRequestError("Add a note, or include the conversation, so HR knows what you need.");
  return { category: b.category, note, ...(conversationId ? { conversationId } : {}), includeConversation };
}

// ---- the email to HR: a notice with a link, never the request itself. HR reads and answers it
// in the portal, so every reply is on the request and tracked (and the employee is told there).

const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

export function escalationEmail(e: Escalation, appUrl: string): { subject: string; text: string; html: string } {
  const link = `${appUrl}/admin/requests/${e.id}`;
  // Not the employee's name: the email is from Ask HR (askhr@novara.com) and shouldn't read as theirs.
  const subject = `New Talk to HR request${requestRef(e)}: ${CATEGORIES[e.category]}`;
  const from = e.channel === "slack" ? " from Slack" : "";
  const text = [
    `${e.requester.name} sent a new Talk to HR request${requestRef(e)}${from}: ${CATEGORIES[e.category]}.`,
    "",
    `Open it in Ask HR to read it and reply: ${link}`,
    "",
    "Reply in Ask HR rather than by email, so the conversation stays on the request and is tracked.",
  ].join("\n");
  const html = [
    `<p><strong>${esc(e.requester.name)}</strong> sent a new Talk to HR request${requestRef(e)}${from}: <strong>${esc(CATEGORIES[e.category])}</strong>.</p>`,
    `<p><a href="${esc(link)}">Open it in Ask HR</a> to read it and reply.</p>`,
    `<p>Reply in Ask HR rather than by email, so the conversation stays on the request and is tracked.</p>`,
  ].join("\n");
  return { subject, text, html };
}

// ---- overdue requests: the inbox's markers and the weekday reminders use the same rules

// Someone waiting on HR longer than this (business hours, weekends don't count) is overdue.
export const REPLY_DUE_BUSINESS_HOURS = 24;
// A request still open after this many days is open too long.
export const OPEN_TOO_LONG_DAYS = 7;

const sharedEvent = (h: EscalationEvent) => h.visibility === "employee" && Boolean(h.note || h.attachments?.length);

// The last thing either side said to the other (HR's comment or the employee's reply).
export const lastShared = (e: Escalation) => e.history.filter(sharedEvent).at(-1);

// HR has written to the employee at least once.
export const hrHasReplied = (e: Escalation) => e.history.some((h) => sharedEvent(h) && h.author !== "employee");

// Someone is waiting on HR: a new request, or an open one where the employee wrote last (or no
// one has written to them yet).
export function needsReply(e: Escalation): boolean {
  if (e.status === "new") return true;
  if (e.status !== "in_progress") return false;
  const last = lastShared(e);
  return !last || last.author === "employee";
}

// Since the employee last wrote, or since they asked.
export function waitingSince(e: Escalation): string {
  return e.history.filter((h) => h.author === "employee").at(-1)?.at ?? e.createdAt;
}

// Hours between two times, leaving out Saturdays and Sundays (UTC days; close enough for "a
// business day" at this scale).
export function businessHoursBetween(fromIso: string, to: Date): number {
  const end = to.getTime();
  let ms = 0;
  for (let t = Date.parse(fromIso); t < end; t += 3600_000) {
    const day = new Date(t).getUTCDay();
    if (day !== 0 && day !== 6) ms += Math.min(3600_000, end - t);
  }
  return ms / 3600_000;
}

export interface Overdue {
  // Waiting on HR too long. firstReply: HR hasn't written to them at all yet.
  reply?: { since: string; businessDays: number; firstReply: boolean };
  // Open too long, whoever it's waiting on.
  open?: { days: number };
}

export function overdue(e: Escalation, now: Date): Overdue {
  if (e.status === "resolved") return {};
  const out: Overdue = {};
  if (needsReply(e)) {
    const since = waitingSince(e);
    const hours = businessHoursBetween(since, now);
    if (hours > REPLY_DUE_BUSINESS_HOURS) out.reply = { since, businessDays: Math.floor(hours / 24), firstReply: !hrHasReplied(e) };
  }
  const days = Math.floor((now.getTime() - Date.parse(e.createdAt)) / 86_400_000);
  if (days >= OPEN_TOO_LONG_DAYS) out.open = { days };
  return out;
}

export const isOverdue = (e: Escalation, now: Date) => {
  const o = overdue(e, now);
  return Boolean(o.reply || o.open);
};

// Why a request is overdue, in a few words: "no reply for 2 business days", "open 9 days".
export function overdueReason(o: Overdue): string {
  const parts: string[] = [];
  if (o.reply) {
    const d = Math.max(1, o.reply.businessDays);
    parts.push(`${o.reply.firstReply ? "no reply" : "their reply unanswered"} for ${d} business day${d === 1 ? "" : "s"}`);
  }
  if (o.open) parts.push(`open ${o.open.days} days`);
  return parts.join(", ");
}

// The weekday reminder: overdue requests grouped by who should hear about them. An owned request
// goes to its owner; one nobody owns goes to HR (hr@ and the HR channel). Who and what kind, with
// a link; never what anyone wrote.
export interface ReminderLine {
  requestId: string;
  number?: number;
  name: string;
  category: Category;
  reason: string;
}

export interface Reminder {
  owner?: { name: string; email: string };
  lines: ReminderLine[];
}

export function reminders(requests: Escalation[], now: Date): Reminder[] {
  const byOwner = new Map<string, Reminder>();
  const late = requests
    .filter((e) => isOverdue(e, now))
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  for (const e of late) {
    const key = e.assignee?.email.toLowerCase() || "";
    const r = byOwner.get(key) ?? (e.assignee?.email ? { owner: { name: e.assignee.name, email: e.assignee.email }, lines: [] } : { lines: [] });
    r.lines.push({
      requestId: e.id,
      ...(e.number ? { number: e.number } : {}),
      name: e.requester.name,
      category: e.category,
      reason: overdueReason(overdue(e, now)),
    });
    byOwner.set(key, r);
  }
  // Owners first, then HR's list of requests nobody owns.
  return [...byOwner.values()].sort((a, b) => Number(!a.owner) - Number(!b.owner));
}

export function reminderText(r: Reminder, appUrl: string): { subject: string; text: string; html: string; slack: string; lines: { text: string; url: string }[] } {
  const base = appUrl.replace(/\/$/, "");
  const n = r.lines.length;
  const lines = r.lines.map((l) => ({
    text: `${l.number ? `#${l.number} ` : ""}${l.name}, ${CATEGORIES[l.category].toLowerCase()}: ${l.reason}`,
    url: `${base}/admin/requests/${l.requestId}`,
  }));
  const whose = r.owner ? "you own" : "nobody owns yet";
  const subject = `Ask HR: ${n} Talk to HR request${n === 1 ? "" : "s"} need${n === 1 ? "s" : ""} attention`;
  const intro = `${n === 1 ? "This request" : "These requests"} ${whose} ${n === 1 ? "is" : "are"} overdue:`;
  const text = [intro, "", ...lines.map((l) => `- ${l.text}\n  ${l.url}`), "", `All open requests: ${base}/admin/requests`].join("\n");
  const html = [
    `<p>${esc(intro)}</p>`,
    `<ul>${lines.map((l) => `<li><a href="${esc(l.url)}">${esc(l.text)}</a></li>`).join("")}</ul>`,
    `<p><a href="${esc(`${base}/admin/requests`)}">All open requests</a></p>`,
  ].join("\n");
  const slackIntro = r.owner
    ? `${n === 1 ? "A Talk to HR request you own is" : `${n} Talk to HR requests you own are`} overdue:`
    : `${n === 1 ? "A Talk to HR request nobody owns is" : `${n} Talk to HR requests nobody owns are`} overdue:`;
  const slack = [slackIntro, ...lines.map((l) => `• <${l.url}|${l.text}>`)].join("\n");
  return { subject, text, html, slack, lines };
}

// Telling someone a request was given to them: email and Slack DM, a link and who asked.
export interface AssignedNotice {
  kind: "notify_assigned";
  email: string;
  requestId: string;
  number?: number;
  category: Category;
  name: string; // who asked
  by: string; // who gave it to them
}

export function assignedNoticeText(n: Omit<AssignedNotice, "kind" | "email">, appUrl: string): { subject: string; text: string; html: string } {
  const link = `${appUrl.replace(/\/$/, "")}/admin/requests/${n.requestId}`;
  const ref = requestRef(n);
  const what = `${n.by} gave you Talk to HR request${ref} from ${n.name}: ${CATEGORIES[n.category]}.`;
  return {
    subject: `Ask HR: request${ref} is yours`,
    text: `${what}\n\nOpen it in Ask HR: ${link}`,
    html: `<p>${esc(what)}</p><p><a href="${esc(link)}">Open it in Ask HR</a></p>`,
  };
}

// The weekday reminder run (the scheduler starts it through the ingest function, which puts this
// on the Slack queue for the worker): overdue requests, and the document misses (misses.ts).
export interface RemindJob {
  kind: "remind";
}
